import { z } from 'zod';
/**
 * Compact IR the LLM is allowed to emit.
 *
 * Far smaller than a full TemplateVersion: the normalizer expands ids, defaults,
 * and engine-specific nesting so the model cannot invent invalid fields as easily.
 */
const hexSchema = z
    .string()
    .regex(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
const fontSpecSchema = z.object({
    family: z.string().min(1).max(80),
    source: z.enum(['google', 'system']).default('google'),
    weights: z.array(z.number().int().min(100).max(900)).min(1).max(6).default([400, 600]),
    fallback: z.string().min(1).max(120).default('system-ui, sans-serif'),
});
/** Models often invent role labels; coerce to the three engine roles. */
function coerceTypeRole(value) {
    if (value === 'display' || value === 'body' || value === 'accent')
        return value;
    const lower = String(value ?? '')
        .trim()
        .toLowerCase();
    if (/display|title|heading|hero|cover|serif|headline/.test(lower))
        return 'display';
    if (/accent|italic|emphasis|script|decorative|cursive/.test(lower))
        return 'accent';
    return 'body';
}
const typeTokenSchema = z.object({
    role: z.union([z.enum(['display', 'body', 'accent']), z.string()]).transform(coerceTypeRole),
    size: z.number().min(8).max(120),
    weight: z.number().int().min(100).max(900),
    lineHeight: z.number().min(0.8).max(2.5).default(1.25),
    letterSpacing: z.number().min(-0.1).max(0.4).optional(),
    textTransform: z.enum(['none', 'uppercase', 'lowercase', 'capitalize']).optional(),
    minSize: z.number().min(6).max(120).optional(),
    maxSize: z.number().min(6).max(120).optional(),
});
export const compactTokensSchema = z.object({
    colors: z
        .object({
        surface: hexSchema,
        surfaceAlt: hexSchema,
        onSurface: hexSchema,
        onSurfaceMuted: hexSchema,
        accent: hexSchema,
        onAccent: hexSchema,
        line: hexSchema,
        overlay: hexSchema,
    })
        .passthrough(),
    fonts: z.object({
        display: fontSpecSchema,
        body: fontSpecSchema,
        accent: fontSpecSchema,
    }),
    typography: z
        .object({
        display: typeTokenSchema,
        itemName: typeTokenSchema,
        itemDescription: typeTokenSchema,
        price: typeTokenSchema,
    })
        .passthrough(),
    spacing: z
        .object({
        xs: z.number().min(0).max(64).default(4),
        sm: z.number().min(0).max(64).default(8),
        md: z.number().min(0).max(96).default(16),
        lg: z.number().min(0).max(128).default(24),
        xl: z.number().min(0).max(200).default(40),
    })
        .passthrough(),
    radii: z
        .object({
        none: z.number().min(0).max(32).default(0),
        md: z.number().min(0).max(48).default(8),
        pill: z.number().min(0).max(999).default(999),
    })
        .passthrough(),
});
const compactFillSchema = z.union([
    z.string().min(1),
    z.object({
        kind: z.literal('solid'),
        color: z.union([z.string(), z.object({ type: z.literal('literal'), hex: hexSchema })]),
    }),
    z.object({
        kind: z.literal('linear'),
        angle: z.number(),
        stops: z
            .array(z.object({
            offset: z.number().min(0).max(1),
            hex: hexSchema,
        }))
            .min(2)
            .max(8),
    }),
]);
const compactNodeSchema = z.lazy(() => z
    .object({
    kind: z.string().min(1),
    name: z.string().optional(),
    direction: z.enum(['row', 'column']).optional(),
    gap: z.union([z.string(), z.number()]).optional(),
    align: z.string().optional(),
    justify: z.string().optional(),
    wrap: z.boolean().optional(),
    columns: z.number().int().min(1).max(8).optional(),
    synthesize: z
        .object({
        minColumns: z.number().int().min(1).max(6),
        maxColumns: z.number().int().min(1).max(6),
        heroRatio: z.number().min(0).max(1),
        maxHeroSpan: z.number().int().min(1).max(4),
        rhythm: z.enum(['uniform', 'alternating', 'golden', 'organic']),
    })
        .optional(),
    entity: z.enum(['wrapper', 'category', 'item']).optional(),
    source: z
        .union([
        z.string(),
        z.object({
            binding: z.string().optional(),
            assetId: z.string().optional(),
            placeholder: z.string().optional(),
            template: z.string().optional(),
            format: z.string().optional(),
            fallback: z.string().optional(),
        }),
    ])
        .optional(),
    as: z.string().optional(),
    typography: z.string().optional(),
    color: z.string().optional(),
    maxLines: z.number().int().min(1).max(20).optional(),
    autoFit: z.boolean().optional(),
    fit: z.enum(['cover', 'contain', 'fill']).optional(),
    aspect: z.number().positive().optional(),
    shape: z.enum(['rect', 'ellipse', 'line']).optional(),
    fill: compactFillSchema.optional(),
    orientation: z.enum(['horizontal', 'vertical']).optional(),
    thickness: z.number().optional(),
    variant: z.string().optional(),
    size: z.union([z.string(), z.number()]).optional(),
    padding: z
        .union([
        z.string(),
        z.object({
            top: z.union([z.string(), z.number()]).optional(),
            right: z.union([z.string(), z.number()]).optional(),
            bottom: z.union([z.string(), z.number()]).optional(),
            left: z.union([z.string(), z.number()]).optional(),
        }),
    ])
        .optional(),
    background: z.string().optional(),
    layout: z
        .object({
        grow: z.number().optional(),
        height: z.union([z.string(), z.number()]).optional(),
        width: z.union([z.string(), z.number()]).optional(),
    })
        .optional(),
    children: z.array(compactNodeSchema).optional(),
    itemTemplate: compactNodeSchema.optional(),
    empty: compactNodeSchema.optional(),
    visibleIf: z.unknown().optional(),
    frame: z
        .object({
        x: z.number(),
        y: z.number(),
        width: z.number(),
        height: z.number(),
        rotate: z.number().optional(),
        z: z.number().optional(),
    })
        .optional(),
})
    .passthrough());
const pageGroupSchema = z.enum([
    'cover',
    'backCover',
    'toc',
    'divider',
    'category',
    'spotlight',
    'filler',
    'info',
]);
export const compactBlueprintSchema = z.object({
    group: pageGroupSchema,
    label: z.string().min(1).max(80),
    variant: z.string().min(1).max(40).default('A'),
    weight: z.number().int().min(0).max(100).default(10),
    capacity: z
        .object({
        min: z.number().int().min(1).max(40),
        preferred: z.number().int().min(1).max(40),
        max: z.number().int().min(1).max(60),
    })
        .optional(),
    constraints: z
        .object({
        minItems: z.number().int().optional(),
        maxItems: z.number().int().optional(),
        minImageCoverage: z.number().min(0).max(1).optional(),
        maxImageCoverage: z.number().min(0).max(1).optional(),
        side: z.enum(['left', 'right', 'any']).optional(),
        firstPageOfCategoryOnly: z.boolean().optional(),
    })
        .optional(),
    /** Layout recipe key the normalizer expands when root is omitted. */
    recipe: z
        .enum([
        'cover-centered',
        'cover-editorial',
        'category-list',
        'category-grid',
        'category-photo',
        'divider-bold',
        'toc-simple',
        'spotlight-hero',
        'info-simple',
        'filler-quiet',
        'back-simple',
    ])
        .optional(),
    root: compactNodeSchema.optional(),
});
export const compactAiDocumentSchema = z.object({
    format: z
        .enum([
        'digital_book',
        'single_page',
        'flyer',
        'table_tent',
        'wall_board',
        'tv_slide',
        'interior_sheet',
        'pocket_menu',
        'social_post',
    ])
        .default('digital_book'),
    tokens: compactTokensSchema,
    blueprints: z.array(compactBlueprintSchema).min(1).max(24),
    flow: z
        .object({
        includeCover: z.boolean().optional(),
        includeBackCover: z.boolean().optional(),
        includeTableOfContents: z.boolean().optional(),
        dividers: z.enum(['none', 'perCategory', 'auto']).optional(),
        includeInfoPage: z.boolean().optional(),
        allowSpotlights: z.boolean().optional(),
        maxSpotlights: z.number().int().min(0).max(8).optional(),
    })
        .optional(),
    /** Asset ids from the allowlist to attach. */
    assetIds: z.array(z.string()).max(40).optional(),
});
const PAGE_GROUPS = new Set([
    'cover',
    'backCover',
    'toc',
    'divider',
    'category',
    'spotlight',
    'filler',
    'info',
]);
/** Map common LLM aliases onto engine page groups. */
function coercePageGroup(value) {
    const raw = String(value ?? '')
        .trim()
        .toLowerCase()
        .replace(/[\s_-]+/g, '');
    const aliases = {
        cover: 'cover',
        front: 'cover',
        frontpage: 'cover',
        frontcover: 'cover',
        back: 'backCover',
        backcover: 'backCover',
        rear: 'backCover',
        toc: 'toc',
        contents: 'toc',
        tableofcontents: 'toc',
        index: 'toc',
        divider: 'divider',
        section: 'divider',
        category: 'category',
        categories: 'category',
        menu: 'category',
        items: 'category',
        list: 'category',
        spotlight: 'spotlight',
        hero: 'spotlight',
        feature: 'spotlight',
        featured: 'spotlight',
        filler: 'filler',
        spacer: 'filler',
        info: 'info',
        about: 'info',
        contact: 'info',
    };
    const mapped = aliases[raw] ?? String(value ?? '');
    return PAGE_GROUPS.has(mapped) ? mapped : String(value ?? '');
}
function defaultTypeToken(key) {
    switch (key) {
        case 'display':
            return { role: 'display', size: 48, weight: 600, lineHeight: 1.05 };
        case 'item':
        case 'itemName':
            return { role: 'body', size: 16, weight: 600, lineHeight: 1.25 };
        case 'itemDescription':
        case 'description':
        case 'body':
            return { role: 'body', size: 13, weight: 400, lineHeight: 1.45 };
        case 'price':
            return { role: 'accent', size: 15, weight: 600, lineHeight: 1.2 };
        default:
            return { role: 'body', size: 14, weight: 400, lineHeight: 1.3 };
    }
}
function coerceTypeToken(key, value) {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        const obj = value;
        // Models sometimes nest `{ typography: "display", size: 48 }` without role.
        if (obj.role == null && typeof obj.typography === 'string') {
            return { ...defaultTypeToken(key), ...obj, role: obj.typography };
        }
        if (obj.role == null) {
            return { ...defaultTypeToken(key), ...obj };
        }
        return obj;
    }
    if (typeof value === 'string' || typeof value === 'number') {
        const base = defaultTypeToken(key);
        if (typeof value === 'string' && value.trim()) {
            return { ...base, role: value };
        }
        if (typeof value === 'number') {
            return { ...base, size: value };
        }
        return base;
    }
    return defaultTypeToken(key);
}
function coerceCapacity(value) {
    if (value == null)
        return undefined;
    if (typeof value === 'number' && Number.isFinite(value)) {
        const preferred = Math.max(1, Math.min(40, Math.round(value)));
        return {
            min: Math.max(1, Math.round(preferred * 0.4)),
            preferred,
            max: Math.min(60, Math.round(preferred * 1.5)),
        };
    }
    if (typeof value === 'object' && !Array.isArray(value))
        return value;
    return undefined;
}
function coerceDividers(value) {
    if (value == null)
        return undefined;
    if (value === 'none' || value === 'perCategory' || value === 'auto')
        return value;
    const raw = String(value)
        .trim()
        .toLowerCase()
        .replace(/[\s-]+/g, '');
    if (raw === 'none' || raw === 'no' || raw === 'false' || raw === '0')
        return 'none';
    if (raw === 'percategory' || raw === 'category' || raw === 'categories')
        return 'perCategory';
    if (raw === 'auto' || raw === 'yes' || raw === 'true' || raw === 'default')
        return 'auto';
    return 'auto';
}
/**
 * Soft-fix shapes models commonly emit before Zod validation.
 * Keeps repair loops for real design mistakes, not trivial type drift.
 */
export function preprocessCompactAiDocument(input) {
    if (!input || typeof input !== 'object' || Array.isArray(input))
        return input;
    const doc = { ...input };
    if (doc.tokens && typeof doc.tokens === 'object' && !Array.isArray(doc.tokens)) {
        const tokens = { ...doc.tokens };
        if (tokens.typography && typeof tokens.typography === 'object' && !Array.isArray(tokens.typography)) {
            const typography = { ...tokens.typography };
            for (const key of Object.keys(typography)) {
                typography[key] = coerceTypeToken(key, typography[key]);
            }
            // Ensure required keys exist even when the model omitted them.
            for (const key of ['display', 'itemName', 'itemDescription', 'price']) {
                if (typography[key] == null)
                    typography[key] = defaultTypeToken(key);
            }
            tokens.typography = typography;
        }
        if (tokens.radii && typeof tokens.radii === 'object' && !Array.isArray(tokens.radii)) {
            const radii = { ...tokens.radii };
            for (const key of Object.keys(radii)) {
                const n = Number(radii[key]);
                if (Number.isFinite(n))
                    radii[key] = Math.max(0, Math.min(999, n));
            }
            tokens.radii = radii;
        }
        doc.tokens = tokens;
    }
    if (Array.isArray(doc.blueprints)) {
        doc.blueprints = doc.blueprints.map((entry) => {
            if (!entry || typeof entry !== 'object' || Array.isArray(entry))
                return entry;
            const bp = { ...entry };
            if (bp.group != null)
                bp.group = coercePageGroup(bp.group);
            if (bp.capacity != null) {
                const capacity = coerceCapacity(bp.capacity);
                if (capacity === undefined)
                    delete bp.capacity;
                else
                    bp.capacity = capacity;
            }
            if (typeof bp.constraints === 'string' || Array.isArray(bp.constraints)) {
                delete bp.constraints;
            }
            if (bp.root != null && (typeof bp.root !== 'object' || Array.isArray(bp.root))) {
                delete bp.root;
            }
            if (!bp.recipe && typeof bp.group === 'string') {
                const recipeByGroup = {
                    cover: 'cover-editorial',
                    backCover: 'back-simple',
                    toc: 'toc-simple',
                    divider: 'divider-bold',
                    category: 'category-list',
                    spotlight: 'spotlight-hero',
                    filler: 'filler-quiet',
                    info: 'info-simple',
                };
                if (recipeByGroup[bp.group])
                    bp.recipe = recipeByGroup[bp.group];
            }
            if (bp.label == null || bp.label === '') {
                bp.label = typeof bp.group === 'string' ? bp.group : 'Page';
            }
            return bp;
        });
    }
    if (doc.flow && typeof doc.flow === 'object' && !Array.isArray(doc.flow)) {
        const flow = { ...doc.flow };
        if (flow.dividers != null)
            flow.dividers = coerceDividers(flow.dividers);
        doc.flow = flow;
    }
    return doc;
}
export function parseCompactAiDocument(input) {
    const result = compactAiDocumentSchema.safeParse(preprocessCompactAiDocument(input));
    if (result.success)
        return { ok: true, data: result.data };
    return {
        ok: false,
        issues: result.error.issues.map((issue) => ({
            path: issue.path.join('.') || '(root)',
            message: issue.message,
        })),
    };
}
//# sourceMappingURL=compact-schema.js.map